import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  allSpecialKeyMetrics,
  getChartsOptions,
  sentimentBrandHealthFormat,
  textShowFun,
} from '../../utils';
import { EchartsCommonBox } from '@op/components/EChartCommon';
import * as echarts from 'echarts';
import { getPercentageInfo } from '@op/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { getChartPosition } from '@op/utils/chart';
import { useIntl, useSelector } from 'umi';
import { NO_DATE } from '@op/constants';
import { decimalFormatPercentage, reviewsFormat } from '@op/utils/value';
import { getChartTimeParams } from '@op/utils/time';
import { chartIconRich, ChartPropsType, getLegendFormat } from '../utils';
import { useChartClickInfo, useXAxisInfo, useYAxisInfo } from '../hooks';
import useChartOptions from '@op/hooks/useChartOptions/useChartOptions';
import EventDetailsPop from './EventDetailsPop';
import BubbleHoverContent from './BubbleHoverContent';
import { debounce } from 'lodash-es';
import { Justify } from 'tea-component';
import { ParamsTimeType } from '@op/types/common';
import ChartsEvents from '@op/components/ChartsEvents';
import './index.scss';
import { useStateRef } from '@op/hooks/common';
import useLanguage from '@op/hooks/useLanguage';

const chartShowEventTopSpace: { [key in ParamsTimeType]: number } = {
  daily: 60,
  weekly: 110,
  monthly: 110,
  hourly: 30,
};

const chartShowHighEventTopSpace: { [key in ParamsTimeType]: number } = {
  daily: 60,
  weekly: 110,
  monthly: 110,
  hourly: 30,
};

export default function BarAndLineCharts({
  tab,
  gameInfo,
  by_type,
  metrics,
  hideMetricsSelect,
  type,
  filter,
  data,
  rightTitle,
  isRatingRatio,
  eventData,
  chartEventSetting,
  leftTitle,
  tooltipConfig,
  height = 540,
  handleEventClick,
  sidebarVisible,
  highPriorityEvents,
  onBarClick,
  blockTitle,
  trendText,
  HasHightEvent, 
}: ChartPropsType) {
  const chartRealType = type === 'stack_line' ? 'line' : (type as any);
  const { formatMessage } = useLanguage();
  const reactEchartsRef = useRef(null);
  const [myChartRef, myChart, setMyChart] = useStateRef<any>();
  const [loadingEvent, setLoadingEvent] = useState(true);

  // hover Bubble 预览内容（渲染函数：每个 Bubble 根据自身的 event 独立生成内容）
  const renderBubbleContent = useCallback(
    (event: any) => (
      <BubbleHoverContent
        event={event}
        trendText={trendText}
        handleEventClick={handleEventClick}
      />
    ),
    [trendText, handleEventClick],
  );
  const { getBottomClickBtn, divElementRef } = useChartClickInfo({
    filter,
    tab,
    by_type,
    hasEventBtn: HasHightEvent && filter.date_type === 'daily',
    eventClick: (params) => {
      onBarClick?.(params.start_time);
    },
  });

  const { getYAxisInfo } = useYAxisInfo({
    type: chartRealType,
    metrics,
    isRatingRatio,
    showEventBubble: chartEventSetting.showEventBubbles,
    data,
  });

  const { getXAxisList, maxTimeValue, showEventAxis } = useXAxisInfo({
    eventData,
    chartEventSetting,
    data,
    filter,
  });

  useEffect(() => {
    setLoadingEvent(true);
  }, [type, formatMessage, chartEventSetting]);

  const options: any = useMemo(() => {
    const { showEventBubbles, bubbleEvent } = chartEventSetting;
    const haveEventBubble = Object.keys(bubbleEvent || {}).length > 0;
    const dateType: ParamsTimeType = filter?.date_type || 'daily';
    const { legend, ...commonChartInfo } = getChartsOptions({
      data: data.yAxisData,
      currentTab: tab,
      gameInfo,
      by_type,
      metrics,
      hideMetricsSelect,
      type,
      chartRealType: chartRealType as any,
      showTotal: isRatingRatio || data?.xAxisData?.length > 20,
      dateType,
      filter,
    });

    let top = 20;
    if (haveEventBubble && showEventBubbles) {
      top += chartShowEventTopSpace[dateType];
    }

    // 只有当 highPriorityEvents 有数据时才预留空间
    const hasHighPriorityEvents =
      highPriorityEvents && Object.keys(highPriorityEvents).length > 0;
    if (hasHighPriorityEvents) {
      top += chartShowHighEventTopSpace[dateType];
    }

    return {
      ...commonChartInfo,
      legend: {
        ...legend,
        itemWidth: 15,
        formatter: getLegendFormat(formatMessage, { byType: by_type }),
        textStyle: { rich: chartIconRich },
      },
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        enterable: true,
        confine: false,
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) => {
          const value = getChartPosition(
            pos,
            params,
            dom,
            rect,
            size,
            100,
            0,
            'bottom',
            3,
          );
          return value;
        },
        formatter: (params: any) => {
          const time = params[0]?.axisValue;
          const leftTitle = listTimeTitleFunc(time, dateType);
          const rightTitle = formatMessage({ id: NO_DATE.EMPTY });
          const specialItem: any[] = [];
          const newParams = params
            .map((item: any) => {
              if (allSpecialKeyMetrics.includes(item.seriesName)) {
                specialItem.push(item);
                return null;
              }
              if (item.seriesName === 'Total') return null;
              if (item.seriesName === 'TopTipsEvent') return null;
              return item;
            })
            .filter(Boolean);

          const textFormatFunc = textShowFun(formatMessage, {
            byType: by_type,
          });

          return formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params: newParams,
            valueFormat: (value: number) => {
              if (isRatingRatio) {
                return decimalFormatPercentage(value, '-', 2);
              }
              return reviewsFormat(value);
            },
            textShowFun: textFormatFunc, // 格式化小星星
            dealwithRecords(records, total) {
              let list: Array<any> = records;
              if (!isRatingRatio) {
                // 处理列表数据，添加占比
                list = records.map(({ originValue, value, ...item }) => {
                  const percent =
                    originValue === 0
                      ? ''
                      : `(${getPercentageInfo(originValue, total, 2)})`;
                  return {
                    value: value + percent,
                    ...item,
                  };
                });
              }
              // 处理特殊数据
              if (specialItem) {
                specialItem.forEach((item: any) => {
                  const { seriesName, value } = item;
                  const format =
                    sentimentBrandHealthFormat[seriesName] || reviewsFormat;
                  const showLast = tooltipConfig?.showSpecialItemLast;
                  if (showLast) {
                    list.push({
                      text: textFormatFunc('', seriesName),
                      value: format(value),
                      originText: seriesName,
                    });
                  } else {
                    list.unshift({
                      text: textFormatFunc('', seriesName),
                      value: format(value),
                      originText: seriesName,
                    });
                  }
                });
              }

              if (
                tooltipConfig === undefined &&
                !['in_total', 'by_rating_ratio'].includes(by_type)
              ) {
                // 添加总量
                list.unshift({
                  text: formatMessage({ id: 'Mentions' }),
                  value: reviewsFormat(total),
                  originText: 'Mentions',
                });
              } else if (Object.keys(tooltipConfig?.total || {})?.length > 0) {
                const { title, valueFormat = reviewsFormat } =
                  tooltipConfig?.total;
                list.unshift({
                  text: formatMessage({ id: title }),
                  value: valueFormat(total),
                  originText: title,
                });
              }

              return list;
            },
            footerText: getBottomClickBtn(getChartTimeParams(time, dateType)),
            isSort: true,
          });
        },
      },
      xAxis: getXAxisList(),
      yAxis: getYAxisInfo(),
      grid: { left: 0, top },
    };
  }, [
    by_type,
    metrics,
    tab,
    hideMetricsSelect,
    type,
    data,
    formatMessage,
    chartEventSetting,
    highPriorityEvents,
  ]);

  const mergeOptions = useChartOptions({
    userOptions: options,
    type: chartRealType,
    excludeOptions: ['xAxis'],
  });

  const timer = useRef<any>(null);

  // 左侧菜单展开/收起 → 父容器宽度变化（带 ~300ms transition）
  // ResizeObserver 在 transition 中可能只捕到中间值，需在 transition 结束后强制 resize
  const menuExpand = useSelector(
    (state: any) => state?.opinionMenu?.menuExpand,
  );
  useEffect(() => {
    let localChart = null;
    if (reactEchartsRef.current) {
      // 复用已存在的实例，避免重复 init 触发 "There is a chart instance already initialized on the dom."
      localChart =
        echarts.getInstanceByDom(reactEchartsRef.current) ||
        echarts.init(reactEchartsRef.current);
    }

    if (localChart) {
      localChart.setOption(mergeOptions, true);

      // 处理一下右边轴不展示的情况，气泡定位出现问题情况
      localChart?.on('legendselectchanged', (params: any) => {
        const { name } = params || {};
        if (allSpecialKeyMetrics.includes(name)) {
          setLoadingEvent(true);
          setTimeout(() => {
            setLoadingEvent(false);
          }, 10);
        }
      });

      localChart?.on('finished', () => {
        if (timer.current !== null) clearTimeout(timer.current);

        timer.current = setTimeout(() => {
          setLoadingEvent(false);
        }, 200);
      });

      // 监听柱子点击事件（仅配置了事件归因的游戏才响应，避免无配置游戏误开右侧弹窗）
      if (onBarClick && HasHightEvent) {
        localChart?.on('click', (params: any) => {
          if (
            params.componentType === 'series' &&
            params.seriesType === 'bar'
          ) {
            const clickedTime = params.name; // xAxis 的值，即时间
            onBarClick(clickedTime);
          }
        });
      }

      if (!myChart) setMyChart(localChart);
    }

    // 添加 resize 事件监听器
    const handleResize = debounce(() => {
      setLoadingEvent(true);
      localChart?.resize();
    }, 500);

    window.addEventListener('resize', handleResize);

    // 监听容器自身尺寸变化（覆盖侧边栏收起/展开等不触发 window.resize 的场景）
    const resizeObserver = new ResizeObserver(handleResize);
    if (reactEchartsRef.current) {
      resizeObserver.observe(reactEchartsRef.current);
    }

    // 左侧菜单 transition 结束后强制 resize（CSS 动画 ~300ms，留 50ms buffer）
    const menuResizeTimer = setTimeout(() => {
      localChart?.resize();
    }, 350);

    return () => {
      // localChart?.off('finished');
      localChart?.off('legendselectchanged');
      localChart?.off('click');
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      clearTimeout(menuResizeTimer);
    };
  }, [mergeOptions, onBarClick, menuExpand, HasHightEvent]);

  // 当侧边栏显示状态变化时，重新调整图表大小
  // useEffect(() => {
  //   if (myChartRef.current) {
  //     setLoadingEvent(true);
  //     myChartRef.current.resize();
  //   }
  // }, [sidebarVisible]);

  if (Object.keys(data || {}).length === 0) return null;
  return (
    <>
      <Justify
        className="dbo-bar-line-title-info"
        left={leftTitle || formatMessage({ id: 'Mentions' })}
        right={rightTitle}
      ></Justify>
      <div ref={divElementRef} style={{ position: 'relative' }}>
        <EchartsCommonBox height={height}>
          <div ref={reactEchartsRef} style={{ width: '100%', height }}></div>
        </EchartsCommonBox>

        {!loadingEvent && (
          <>
            {(chartEventSetting.showEventBubbles ||
              (highPriorityEvents &&
                Object.keys(highPriorityEvents).length > 0)) && (
              <ChartsEvents
                myChart={myChart}
                highPriorityEvents={highPriorityEvents}
                eventList={
                  chartEventSetting.showEventBubbles
                    ? chartEventSetting.bubbleEvent
                    : {}
                }
                format={eventData?.format || ''}
                maxTimeValue={maxTimeValue}
                showEventAxis={showEventAxis}
                dateType={filter.date_type}
                xAxisData={data.xAxisData}
                highEventsClick={handleEventClick}
                bottomSafeZone={30}
                useBubbleText={false}
                bubbleContent={renderBubbleContent}
                // onBubbleVisibleChange={(visible, event) => {
                //   if (visible && onBubbleHoverFetch) {
                //     onBubbleHoverFetch(event);
                //   }
                // }}
                highPriorityEventDirection="end"
                blockTitle={blockTitle}
                trendText={trendText}
              ></ChartsEvents>
            )}
            {chartEventSetting.showEventAxis && (
              <EventDetailsPop
                myChart={myChart}
                dateType={filter.date_type}
                eventList={eventData?.chartRenderEventList}
              ></EventDetailsPop>
            )}
          </>
        )}
      </div>
    </>
  );
}
